import { ChangePasswordDialog } from '../pages/StaffPage';
import { OfflineBanner } from './resilience';
import { DASHBOARD_TITLES, RouteA11y } from './RouteA11y';
import { useState, Suspense } from 'react';
import { NavLink, Outlet } from 'react-router';
import { KeyRound, LogOut } from 'lucide-react';
import logoMark from '@hutdrop/design-tokens/logo-mark.svg';
import { api } from '../lib/api';
import { useStaffAuth } from '../lib/auth';
import { NAV } from '../nav';
import { JobHealthBanner } from './JobHealthBanner';
import { useOrderCounts } from '../lib/orders';
import { CounterSwitches, NewOrderAlert } from './NewOrderAlert';

const ROLE_LABEL = { EMPLOYEE: 'Employee', MANAGER: 'Manager', ADMIN: 'Owner / Admin', CUSTOMER: 'Customer' } as const;

export function Shell() {
  const { staff, can } = useStaffAuth();
  const [signingOut, setSigningOut] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const items = NAV.filter((n) => can(n.permission));
  const user = staff!.user;
  const counts = useOrderCounts(can('orders.view'));
  // Orders waiting on staff: new ones to gather and packed ones waiting at the counter.
  const waiting = (counts.data?.new ?? 0) + (counts.data?.ready ?? 0);

  return (
    <div className="layout">
      <aside className="sidebar">
        <div className="sidebar__brand">
          <img src={logoMark} width={26} height={26} alt="" />
          <span>HutDrop</span>
        </div>
        <nav aria-label="Store dashboard" className="sidebar__nav">
          {items.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} end={to === '/'} className="navlink">
              <Icon size={18} aria-hidden="true" />
              <span>{label}</span>
              {to === '/orders' && waiting > 0 ? (
                <span className="nav-count" aria-label={`${waiting} waiting`}>
                  {waiting}
                </span>
              ) : null}
            </NavLink>
          ))}
        </nav>
        {can('orders.view') ? <CounterSwitches /> : null}
        <div className="sidebar__user">
          <div className="avatar" aria-hidden="true">
            {user.firstName.charAt(0)}
            {user.lastName?.charAt(0) ?? ''}
          </div>
          <div className="sidebar__user-text">
            <span className="sidebar__user-name">
              {user.firstName} {user.lastName ?? ''}
            </span>
            <span className="sidebar__user-role">{ROLE_LABEL[user.role]}</span>
          </div>
          <button className="icon-btn" aria-label="Change password" title="Change password" onClick={() => setChangingPassword(true)}>
            <KeyRound size={18} aria-hidden="true" />
          </button>
          <button
            className="icon-btn"
            aria-label="Sign out"
            title="Sign out"
            disabled={signingOut}
            onClick={async () => {
              setSigningOut(true);
              try {
                await api.logout();
              } finally {
                setSigningOut(false);
              }
            }}
          >
            <LogOut size={18} aria-hidden="true" />
          </button>
        </div>
      </aside>
      <RouteA11y appName="HutDrop Store" titles={DASHBOARD_TITLES} />
      <main className="main" id="main" tabIndex={-1}>
        <OfflineBanner />
        {can('orders.view') ? <NewOrderAlert counts={counts.data} /> : null}
        <JobHealthBanner />
        <Suspense fallback={<div className="table-skeleton" aria-busy="true" aria-label="Loading"><div className="skeleton-row" /><div className="skeleton-row" /><div className="skeleton-row" /></div>}>
          <Outlet />
        </Suspense>
      </main>
      <ChangePasswordDialog open={changingPassword} onClose={() => setChangingPassword(false)} />
    </div>
  );
}
